import { isNumber } from 'lodash';
import { SalesCampaignOverviewRow } from 'src/domain/table/sales-campaign-overview.row';

export interface SummaryData {
    totalExpectedSalesIv: number;
    totalStockValue: number;
    totalStockValueOnSale: number;
    totalStockValueNotOnSale: number;
    totalStockQuantity: number;
    totalStockQuantityOnSale: number;
    totalStockQuantityNotOnSale: number;
    percentageStockValueOnSale: number;
    percentageStockQuantityOnSale: number;
    avgMarkDown: number;
    stylesOnSale: number;
    styleOptionsOnSale: number;
    totalSalesPrice: number;
    totalRecommendedRetailPrice: number;
    totalSalesPriceOnSale: number;
    totalCogs: number;
    totalCogsOnSale: number;
}

interface SalesCampaignOverviewRowWithNewSalesPrice extends Omit<SalesCampaignOverviewRow, 'newSalesPrice'> {
    newSalesPrice: number;
}

export const calculateSummary = (rows: SalesCampaignOverviewRow[]): SummaryData => {
    const salesCampaigns = rows.filter((row) => isNumber(row.recommendedRetailPriceLcy) && row.stockQuantity > 0);
    const onSale = salesCampaigns.filter((row) => isOnSale(row));

    // ?? Could use rows, is the stockvalue i guess its not related to rrp
    const totalStockValue = salesCampaigns.reduce((acc, row) => acc + row.stockValueLcy, 0);
    const totalStockValueOnSale = onSale.reduce((acc, row) => {
        return acc + row.stockValueLcy;
    }, 0);

    const totalStockQuantity = salesCampaigns.reduce((acc, row) => acc + row.stockQuantity, 0);
    const totalStockQuantityOnSale = onSale.reduce((acc, row) => {
        return acc + row.stockQuantity;
    }, 0);

    const totalEpectedSalesIv = onSale.reduce((acc, row) => {
        const itemPrice = row.newSalesPrice;
        const totalPrice = itemPrice * row.stockQuantity;
        return acc + totalPrice;
    }, 0);

    const totalStockValueNotOnSale = totalStockValue - totalStockValueOnSale;
    const totalStockQuantityNotOnSale = totalStockQuantity - totalStockQuantityOnSale;
    const percentageStockValueOnSale = totalStockValue > 0 ? (totalStockValueOnSale / totalStockValue) * 100 : 0;
    const percentageStockQuantityOnSale =
        totalStockQuantity > 0 ? (totalStockQuantityOnSale / totalStockQuantity) * 100 : 0;

    const totalSalesPrice = salesCampaigns.reduce((acc, row) => {
        if (isOnSale(row)) {
            return acc + row.newSalesPrice;
        }

        if (isNumber(row.recommendedRetailPriceLcy)) {
            return acc + row.recommendedRetailPriceLcy;
        }

        return acc;
    }, 0);
    const totalSalesPriceOnSale = onSale.reduce((acc, row) => {
        return acc + row.newSalesPrice;
    }, 0);

    const totalRecommendedRetailPrice = salesCampaigns.reduce((acc, row) => {
        return isNumber(row.recommendedRetailPriceLcy) ? acc + row.recommendedRetailPriceLcy : acc;
    }, 0);
    const totalRecommendedRetailPriceOnSale = onSale.reduce((acc, row) => {
        return isNumber(row.recommendedRetailPriceLcy) ? acc + row.recommendedRetailPriceLcy : acc;
    }, 0);

    const totalCogs = salesCampaigns.reduce((acc, row) => {
        if (row.stockQuantity > 0) {
            return acc + row.stockValueLcy / row.stockQuantity;
        }
        return acc;
    }, 0);

    const totalCogsOnSale = onSale.reduce((acc, row) => {
        if (isOnSale(row) && row.stockQuantity > 0) {
            return acc + row.stockValueLcy / row.stockQuantity;
        }
        return acc;
    }, 0);

    const avgMarkDown =
        totalRecommendedRetailPriceOnSale !== 0
            ? ((totalRecommendedRetailPriceOnSale - totalSalesPriceOnSale) / totalRecommendedRetailPriceOnSale) * 100
            : 0;

    const stylesOnSale = onSale.reduce<number[]>((acc, row) => {
        if (!acc.includes(row.styleNumber)) {
            acc.push(row.styleNumber);
        }
        return acc;
    }, []);

    return {
        percentageStockQuantityOnSale,
        percentageStockValueOnSale,
        totalExpectedSalesIv: totalEpectedSalesIv,
        totalStockQuantity,
        totalStockQuantityNotOnSale,
        totalStockQuantityOnSale,
        totalStockValue,
        totalStockValueNotOnSale,
        totalStockValueOnSale,
        avgMarkDown,
        stylesOnSale: stylesOnSale.length,
        styleOptionsOnSale: onSale.length,
        totalSalesPrice,
        totalSalesPriceOnSale,
        totalRecommendedRetailPrice,
        totalCogs,
        totalCogsOnSale,
    };
};

export function isOnSale(row: SalesCampaignOverviewRow): row is SalesCampaignOverviewRowWithNewSalesPrice {
    return isNumber(row.newSalesPrice);
}
